/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import React from "react";
function initializeShownOptionalArguments(argumentOperands, operandValuesByOperandName) {
  const result = new Set();
  for (const argumentOperand of argumentOperands) {
    if (GuidedExpressionEditorUtils.findArgumentOperandValue(argumentOperand, operandValuesByOperandName) != null) {
      result.add(argumentOperand.argumentIdentifier);
    }
  }
  return result;
}
function reduceOptionalArguments(previousState, action) {
  if (action.props.isReadonly) {
    return previousState;
  }
  const copy = new Set(previousState);
  if (action.action === "show") {
    copy.add(action.argumentOperand.argumentIdentifier);
  } else if (action.action === "hide") {
    let updatedOperandValues = action.props.operandValuesByOperandName;
    for (const expArgumentOperand of action.argumentOperand.expArgumentOperands) {
      updatedOperandValues = updatedOperandValues.delete(expArgumentOperand.name);
    }
    action.props.applyArgumentOperands(updatedOperandValues);
    copy.delete(action.argumentOperand.argumentIdentifier);
  } else if (action.action === "set") {
    let updatedOperandValues = action.props.operandValuesByOperandName.clear();
    const updatedIdentifiers = new Set();
    for (const argumentOperand of action.argumentOperands) {
      updatedIdentifiers.add(argumentOperand.argumentIdentifier);
      for (const expArgumentOperand of argumentOperand.expArgumentOperands) {
        updatedOperandValues = updatedOperandValues.set(expArgumentOperand.name, action.props.operandValuesByOperandName.get(expArgumentOperand.name));
      }
    }
    action.props.applyArgumentOperands(updatedOperandValues);
    return updatedIdentifiers;
  }
  return copy;
}

/**
 * Manages the visibility of optional argument operands.
 * The child components of this component have access to the OptionalArgumentManager.Context with
 * the visibility state and functions to change visibility.
 */
export function OptionalArgumentManager(props) {
  const [shownOptionalIdentifiers, dispatchOptionalAction] = React.useReducer(reduceOptionalArguments, undefined, () => initializeShownOptionalArguments(props.argumentOperands, props.operandValuesByOperandName));
  const context = React.useMemo(() => {
    return {
      shownOptionalArgumentIdentifiers: shownOptionalIdentifiers,
      showArgument: argumentIdentifier => {
        const argumentOperand = props.argumentOperands.find(operand => operand.argumentIdentifier === argumentIdentifier);
        if (argumentOperand == null) {
          return;
        }
        dispatchOptionalAction({
          action: "show",
          argumentOperand,
          props
        });
      },
      hideArgument: argumentIdentifier => {
        const argumentOperand = props.argumentOperands.find(operand => operand.argumentIdentifier === argumentIdentifier);
        if (argumentOperand == null) {
          return;
        }
        dispatchOptionalAction({
          action: "hide",
          argumentOperand,
          props
        });
      },
      setShownArguments: argumentIdentifiers => {
        const argumentOperands = props.argumentOperands.filter(operand => argumentIdentifiers.has(operand.argumentIdentifier));
        dispatchOptionalAction({
          action: "set",
          argumentOperands,
          props
        });
      }
    };
  }, [shownOptionalIdentifiers, props]);
  return /*#__PURE__*/React.createElement(OptionalArgumentManager.Context.Provider, {
    value: context
  }, props.children);
}
(function (_OptionalArgumentManager) {
  const Context = _OptionalArgumentManager.Context = /*#__PURE__*/React.createContext({
    shownOptionalArgumentIdentifiers: new Set(),
    showArgument: () => {},
    hideArgument: () => {},
    setShownArguments: () => {}
  });
})(OptionalArgumentManager || (OptionalArgumentManager = {}));
//# sourceMappingURL=OptionalArgumentManager.js.map
